import { useEffect, useState } from "react";
import { useLocation, Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useAdminAuth } from "@/contexts/AdminAuthContext";
import { getDashboardRouteForRole } from "@/utils/roleRedirects";
import { supabase } from "@/integrations/supabase/client";

export default function DashboardRedirect() {
  const location = useLocation();
  const { user: patientUser, loading: patientLoading } = useAuth();
  const { user: adminUser, loading: adminLoading } = useAdminAuth();
  const [actualRole, setActualRole] = useState<string | null>(null);
  const [roleLoading, setRoleLoading] = useState(true);

  const loading = patientLoading || adminLoading;
  const currentUser = adminUser || patientUser;

  // Fetch actual role from user_roles table (single source of truth)
  useEffect(() => {
    const fetchActualRole = async () => {
      if (!currentUser?.id) {
        setRoleLoading(false);
        return;
      }

      // First check admin context (most reliable after login)
      if (adminUser?.role) {
        setActualRole(adminUser.role);
        setRoleLoading(false);
        return;
      }

      // Query user_roles table for actual role
      try {
        const { data, error } = await supabase
          .from('user_roles')
          .select('role')
          .eq('user_id', currentUser.id);

        if (!error && data && data.length > 0) {
          // Priority: super_admin > agency_admin > supervisor > patient
          const roles = data.map(r => r.role);
          if (roles.includes('super_admin')) {
            setActualRole('super_admin');
          } else if (roles.includes('agency_admin')) {
            setActualRole('agency_admin');
          } else if (roles.includes('supervisor')) {
            setActualRole('supervisor');
          } else {
            setActualRole('patient');
          }
        } else {
          // Fallback to patientUser.role
          setActualRole(patientUser?.role || 'patient');
        }
      } catch {
        setActualRole(patientUser?.role || 'patient');
      } finally {
        setRoleLoading(false);
      }
    };

    if (!loading) {
      fetchActualRole();
    }
  }, [currentUser?.id, adminUser?.role, patientUser?.role, loading]);

  if (loading || roleLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }

  if (!currentUser || !actualRole) {
    const intended = `${location.pathname}${location.search || ""}`;
    return <Navigate to={`/login?redirect=${encodeURIComponent(intended)}`} replace />;
  }

  return <Navigate to={getDashboardRouteForRole(actualRole)} replace />;
}
